import { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import {
  Coins,
  FileUp,
  Landmark,
  Layers,
  Plus,
  Scale,
  Search,
  Trash2,
  TrendingDown,
  TrendingUp,
  Wallet,
  X,
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { SelectNative } from "../components/ui/select-native";
import { Card, CardContent } from "../components/ui/card";
import { CurrencyHint } from "../components/CurrencyHint";
import { CasImportDialog } from "./CasImportDialog";
import { DeleteConfirmDialog } from "../components/DeleteConfirmDialog";
import {
  bulkDeleteAssets,
  bulkDeleteLiabilities,
  createAsset,
  createLiability,
  deleteAsset,
  deleteLiability,
  fetchAssets,
  fetchAssetTypes,
  fetchLiabilities,
  fetchLiabilityTypes,
  fetchNetWorth,
  updateAsset,
  updateLiability,
} from "../lib/api";
import { normalizeDetail } from "../lib/errors";

function formatINR(n) {
  if (n == null || n === "" || Number.isNaN(Number(n))) return "—";
  return `₹ ${new Intl.NumberFormat("en-IN", { maximumFractionDigits: 0 }).format(Number(n))}`;
}

// Compact INR (₹1.5L, ₹1.2Cr) for the sub-tab pill labels — avoids overflowing the tab row.
function formatINRCompact(n) {
  const v = Number(n) || 0;
  if (v >= 1e7) return `₹${(v / 1e7).toFixed(1)}Cr`;
  if (v >= 1e5) return `₹${(v / 1e5).toFixed(1)}L`;
  if (v >= 1e3) return `₹${(v / 1e3).toFixed(0)}K`;
  return `₹${v.toFixed(0)}`;
}

/** Combined page: Assets tab, Liabilities tab, and a Net Worth summary strip at the top.
 *
 * The active tab is mirrored to the URL (`?tab=assets|liabilities`) so deep-links from the
 * dashboard tiles land on the right tab and the browser Back button restores the previous
 * selection. Only these two values are honoured; anything else falls back to "assets". */
export function NetWorthPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const urlTab = searchParams.get("tab");
  const initialTab = urlTab === "liabilities" ? "liabilities" : "assets";
  const [tab, setTab] = useState(initialTab);
  const [summary, setSummary] = useState(null);

  // Keep local state in sync when the URL changes underneath us (dashboard tile click, back
  // button, direct link). Only reacts to actual value changes so it never fights user clicks.
  useEffect(() => {
    const next = urlTab === "liabilities" ? "liabilities" : "assets";
    setTab((cur) => (cur === next ? cur : next));
  }, [urlTab]);

  const changeTab = useCallback(
    (k) => {
      setTab(k);
      // `replace` avoids polluting history — the tab switch is a UI toggle, not navigation.
      setSearchParams(k === "assets" ? {} : { tab: k }, { replace: true });
    },
    [setSearchParams],
  );

  const loadSummary = useCallback(async () => {
    try {
      setSummary(await fetchNetWorth());
    } catch (e) {
      // eslint-disable-next-line no-console
      console.warn("net-worth summary load failed", e);
    }
  }, []);

  useEffect(() => { loadSummary(); }, [loadSummary]);

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 lg:py-10" data-testid="net-worth-page">
      <div className="flex flex-col gap-2">
        <div className="inline-flex items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-gold">
          <span className="h-px w-6 bg-gold/60" />
          Net Worth
        </div>
        <h1 className="font-serif text-3xl text-text sm:text-4xl">
          Your <span className="text-gold">balance sheet</span>
        </h1>
        <p className="max-w-2xl text-sm text-muted">
          Track every asset and liability. Enter what you paid and what it&apos;s worth today —
          the gain shown is real, not a projected rate.
        </p>
      </div>

      <NetWorthSummary summary={summary} />

      <div className="mt-8 flex gap-2 border-b border-border">
        {[
          { k: "assets", label: "Assets" },
          { k: "liabilities", label: "Liabilities" },
        ].map((t) => (
          <button
            key={t.k}
            type="button"
            onClick={() => changeTab(t.k)}
            data-testid={`nw-tab-${t.k}`}
            className={`border-b-2 px-4 py-2 text-sm font-medium ${
              tab === t.k
                ? "border-gold text-gold"
                : "border-transparent text-muted hover:text-text"
            }`}
          >
            {t.label}
          </button>
        ))}
      </div>

      <div className="mt-6">
        {tab === "assets" ? (
          <AssetsTab onChange={loadSummary} />
        ) : (
          <LiabilitiesTab onChange={loadSummary} />
        )}
      </div>
    </div>
  );
}

/* ------------------------------ Net Worth Summary ------------------------------ */

function SummaryTile({ icon: Icon, label, value, tone = "default", testId }) {
  const toneClass =
    tone === "gold"
      ? "border-gold/40 bg-gold/5 text-gold"
      : tone === "danger"
      ? "border-red-500/30 bg-red-500/5 text-red-300"
      : "border-border bg-elevated/40 text-text";
  return (
    <div className={`flex flex-col gap-2 rounded-lg border p-5 ${toneClass}`} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-muted">
        <Icon className="h-3.5 w-3.5" />
        {label}
      </div>
      <div className="text-2xl font-semibold sm:text-3xl">{value}</div>
    </div>
  );
}

function NetWorthSummary({ summary }) {
  const total = summary?.total_assets ?? 0;
  const liab = summary?.total_liabilities ?? 0;
  const net = summary?.net_worth ?? 0;
  return (
    <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      <SummaryTile
        icon={Scale}
        label="Net worth"
        tone="gold"
        value={formatINR(net)}
        testId="summary-net-worth"
      />
      <SummaryTile
        icon={TrendingUp}
        label={`Total assets (${summary?.asset_count ?? 0})`}
        value={formatINR(total)}
        testId="summary-total-assets"
      />
      <SummaryTile
        icon={TrendingDown}
        label={`Total liabilities (${summary?.liability_count ?? 0})`}
        tone="danger"
        value={formatINR(liab)}
        testId="summary-total-liabilities"
      />
      <SummaryTile
        icon={Wallet}
        label="Liquid (high liquidity)"
        value={formatINR(summary?.liquid_total ?? 0)}
        testId="summary-liquid"
      />
    </div>
  );
}

/* --------------------------------- Assets Tab --------------------------------- */

const ASSET_EMPTY = {
  asset_type_id: "",
  name: "",
  purchase_date: "",
  purchase_price: "",
  current_value: "",
  notes: "",
};

// Ordered bucket definitions. Each row: {key, label, match(asset)}. The first bucket that
// matches wins, so "Mutual Fund — Equity" doesn't leak into "Equities". "All" is prepended
// at render time. Only buckets that contain at least one asset are shown as tabs.
const ASSET_BUCKETS = [
  { key: "real_estate", label: "Real Estate", match: (a) => (a.category || "").toLowerCase() === "real estate" },
  { key: "mutual_funds", label: "Mutual Funds", match: (a) => (a.item_type || "").toLowerCase().startsWith("mutual fund") },
  { key: "etfs", label: "ETFs", match: (a) => (a.item_type || "").toLowerCase() === "etf" },
  { key: "equities", label: "Equities", match: (a) => (a.item_type || "").toLowerCase() === "equity" },
  { key: "bonds", label: "Bonds", match: (a) => (a.item_type || "").toLowerCase() === "bonds" },
  { key: "fd_rd", label: "FD / RD / PPF", match: (a) => {
      const t = (a.item_type || "").toLowerCase();
      return t === "fixed deposit" || t === "recurring deposit" || t === "ppf";
    } },
  { key: "crypto", label: "Crypto", match: (a) => (a.item_type || "").toLowerCase() === "cryptocurrency" },
  { key: "gold_silver", label: "Gold & Silver", match: (a) => (a.category || "").toLowerCase() === "precious metals" },
  { key: "cash", label: "Cash & Bank", match: (a) => (a.category || "").toLowerCase() === "cash" },
  { key: "vehicles", label: "Vehicles", match: (a) => (a.category || "").toLowerCase() === "vehicles" },
  { key: "electronics", label: "Electronics", match: (a) => (a.category || "").toLowerCase() === "electronics" },
];

function bucketFor(asset) {
  const b = ASSET_BUCKETS.find((x) => x.match(asset));
  return b ? b.key : "other";
}

function AssetsTab({ onChange }) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [casOpen, setCasOpen] = useState(false);
  const [activeBucket, setActiveBucket] = useState("all");
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid"); // "grid" | "list"
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDialog, setConfirmDialog] = useState(null); // { mode: "single"|"bulk", row?, ids? }
  const [busyDelete, setBusyDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([fetchAssetTypes(), fetchAssets()]);
      setTypes(t);
      setRows(r);
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't load assets."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(row) {
    setConfirmDialog({ mode: "single", row });
  }

  async function confirmSingleAssetDelete() {
    const row = confirmDialog?.row;
    if (!row) return;
    setBusyDelete(true);
    try {
      await deleteAsset(row.id);
      toast.success("Deleted.");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      await load();
      await onChange?.();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete."));
    } finally {
      setBusyDelete(false);
      setConfirmDialog(null);
    }
  }

  async function confirmBulkAssetDelete() {
    const ids = confirmDialog?.ids || [];
    if (!ids.length) return;
    setBusyDelete(true);
    try {
      const { deleted } = await bulkDeleteAssets(ids);
      toast.success(`${deleted} asset${deleted === 1 ? "" : "s"} deleted.`);
      setSelectedIds(new Set());
      await load();
      await onChange?.();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete the selected assets."));
    } finally {
      setBusyDelete(false);
      setConfirmDialog(null);
    }
  }

  // ---------- Selection helpers ----------
  // Prune stale selections (rows removed by the bucket filter / search / load).
  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(rows.map((r) => r.id));
      let dirty = false;
      const next = new Set();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else dirty = true;
      }
      return dirty ? next : prev;
    });
  }, [rows]);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const total = useMemo(
    () => rows.reduce((s, a) => s + Number(a.current_value || 0), 0),
    [rows]
  );

  // Per-bucket stats — only buckets with ≥1 asset become tabs. "Other" catches anything
  // that didn't match a known bucket (custom admin categories, uncategorized).
  const bucketStats = useMemo(() => {
    const counts = new Map();
    const totals = new Map();
    for (const a of rows) {
      const key = bucketFor(a);
      counts.set(key, (counts.get(key) || 0) + 1);
      totals.set(key, (totals.get(key) || 0) + Number(a.current_value || 0));
    }
    const known = ASSET_BUCKETS.filter((b) => counts.has(b.key)).map((b) => ({
      key: b.key,
      label: b.label,
      count: counts.get(b.key),
      total: totals.get(b.key),
    }));
    if (counts.has("other")) {
      known.push({ key: "other", label: "Other", count: counts.get("other"), total: totals.get("other") });
    }
    return known;
  }, [rows]);

  const filteredRows = useMemo(() => {
    let list = rows;
    if (activeBucket !== "all") list = list.filter((a) => bucketFor(a) === activeBucket);
    const q = query.trim().toLowerCase();
    if (q) {
      list = list.filter((a) => {
        // Case-insensitive substring across name + type + category. Matches "gold" against
        // the name "SGB 2030" as long as category is "Gold" — the label search users expect.
        const hay = `${a.name || ""} ${a.item_type || ""} ${a.category || ""}`.toLowerCase();
        return hay.includes(q);
      });
    }
    return list;
  }, [rows, activeBucket, query]);

  // Selection helpers that depend on filteredRows — declared AFTER filteredRows to avoid
  // a temporal-dead-zone crash in the initial render.
  const visibleIds = useMemo(() => filteredRows.map((r) => r.id), [filteredRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  // Reset filter to "all" when the active bucket no longer has any assets (e.g. after delete).
  useEffect(() => {
    if (activeBucket !== "all" && !bucketStats.some((b) => b.key === activeBucket)) {
      setActiveBucket("all");
    }
  }, [bucketStats, activeBucket]);

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">
          {rows.length} asset{rows.length === 1 ? "" : "s"} · Total {formatINR(total)}
        </div>
        <div className="flex flex-wrap gap-2">
          <Link to="/app/portfolio/timeline">
            <Button
              variant="outline"
              data-testid="networth-timeline-cta"
              className="gap-2"
            >
              <TrendingUp className="h-4 w-4" /> Portfolio timeline
            </Button>
          </Link>
          <Button
            variant="outline"
            onClick={() => setCasOpen(true)}
            data-testid="cas-import-open"
            className="gap-2"
            disabled={!types.length}
          >
            <FileUp className="h-4 w-4" /> Import from CAS
          </Button>
          <Button
            onClick={() => setDialog({ mode: "create" })}
            data-testid="asset-add-btn"
            className="gap-2"
            disabled={!types.length}
          >
            <Plus className="h-4 w-4" /> Add asset
          </Button>
        </div>
      </div>

      {!types.length && !loading && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-200">
          No asset types are configured yet. Please ask an admin to set up the master taxonomy.
        </div>
      )}

      {rows.length > 0 && (
        <div
          className="-mx-1 flex flex-wrap gap-1.5 overflow-x-auto pb-1"
          data-testid="asset-bucket-tabs"
          role="tablist"
        >
          <BucketTab
            active={activeBucket === "all"}
            label="All"
            count={rows.length}
            total={total}
            onClick={() => setActiveBucket("all")}
            testKey="all"
          />
          {bucketStats.map((b) => (
            <BucketTab
              key={b.key}
              active={activeBucket === b.key}
              label={b.label}
              count={b.count}
              total={b.total}
              onClick={() => setActiveBucket(b.key)}
              testKey={b.key}
            />
          ))}
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="Search assets by name, type or category…"
            testId="asset-search"
          />
          {/* View toggle: switches the current bucket between card grid and dense list */}
          <div className="flex items-center gap-1 rounded-md border border-border bg-elevated/40 p-1 text-[10px]">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`rounded px-2.5 py-1 uppercase tracking-widest ${viewMode === "grid" ? "bg-gold/15 text-gold" : "text-muted hover:text-text"}`}
              data-testid="asset-view-grid"
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`rounded px-2.5 py-1 uppercase tracking-widest ${viewMode === "list" ? "bg-gold/15 text-gold" : "text-muted hover:text-text"}`}
              data-testid="asset-view-list"
            >
              List
            </button>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2"
          data-testid="asset-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-semibold text-gold">{selectedIds.size}</span> asset
            {selectedIds.size === 1 ? "" : "s"} selected
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              data-testid="asset-bulk-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDialog({ mode: "bulk", ids: [...selectedIds] })}
              data-testid="asset-bulk-delete"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-border p-8 text-center text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted">
          No assets yet. Add your first investment, gold, property or vehicle to start
          building your balance sheet.
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted">
          {query.trim()
            ? `No assets match "${query.trim()}".`
            : "No assets in this bucket yet."}
        </div>
      ) : viewMode === "list" ? (
        <AssetListView
          rows={filteredRows}
          allSelected={allVisibleSelected}
          someSelected={someVisibleSelected}
          onToggleAll={toggleSelectAllVisible}
          selectedIds={selectedIds}
          onToggle={toggleSelect}
          onEdit={(r) => setDialog({ mode: "edit", row: r })}
          onDelete={handleDelete}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3" data-testid={`asset-grid-${activeBucket}`}>
          {filteredRows.map((r) => (
            <AssetCard
              key={r.id}
              asset={r}
              selected={selectedIds.has(r.id)}
              onToggleSelect={() => toggleSelect(r.id)}
              onEdit={() => setDialog({ mode: "edit", row: r })}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <AssetDialog
          initial={dialog.row}
          mode={dialog.mode}
          types={types}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await load();
            await onChange?.();
          }}
        />
      )}

      <CasImportDialog
        open={casOpen}
        onClose={() => setCasOpen(false)}
        onImported={async () => {
          await load();
          await onChange?.();
        }}
      />

      <DeleteConfirmDialog
        open={confirmDialog?.mode === "single"}
        busy={busyDelete}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={confirmSingleAssetDelete}
        title={`Delete "${confirmDialog?.row?.name || "asset"}"?`}
        description="This permanently removes the asset from your balance sheet. This can't be undone."
        confirmLabel="Yes, delete"
        testId="asset-single-delete-dialog"
      />
      <DeleteConfirmDialog
        open={confirmDialog?.mode === "bulk"}
        busy={busyDelete}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={confirmBulkAssetDelete}
        title={`Delete ${confirmDialog?.ids?.length || 0} asset${confirmDialog?.ids?.length === 1 ? "" : "s"}?`}
        description="Every selected asset will be permanently removed from your balance sheet. To prevent accidental bulk deletion, please type DELETE below to confirm."
        requireType
        confirmLabel="Yes, delete all"
        testId="asset-bulk-delete-dialog"
      />
    </div>
  );
}

function SearchBox({ value, onChange, placeholder, testId }) {
  // Compact leading-icon input reused by AssetsTab + LiabilitiesTab. `<X>` clear button
  // only appears when there's something to clear so it doesn't add visual noise on load.
  return (
    <div className="relative w-full sm:max-w-sm">
      <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
      <Input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        className="pl-9 pr-9"
        data-testid={testId}
      />
      {value && (
        <button
          type="button"
          onClick={() => onChange("")}
          aria-label="Clear search"
          data-testid={`${testId}-clear`}
          className="absolute right-2 top-1/2 -translate-y-1/2 rounded p-1 text-muted hover:text-text"
        >
          <X className="h-3.5 w-3.5" />
        </button>
      )}
    </div>
  );
}

function DenseListTable({
  rows,
  columns,          // [{ label, className?, get: (row) => node }]
  keyFor,           // (row) => id
  allSelected,
  someSelected,
  onToggleAll,
  selectedIds,
  onToggle,
  onEdit,
  onDelete,
  testId,
}) {
  // Reusable compact table used by both Assets and Liabilities in list mode.
  return (
    <div className="overflow-x-auto rounded-lg border border-border" data-testid={testId}>
      <table className="w-full text-sm">
        <thead className="border-b border-border bg-elevated/40 text-left text-[10px] uppercase tracking-widest text-muted">
          <tr>
            <th className="w-10 px-3 py-2.5">
              <input
                type="checkbox"
                aria-label="Select all visible"
                checked={allSelected}
                ref={(el) => { if (el) el.indeterminate = someSelected; }}
                onChange={onToggleAll}
                data-testid={`${testId}-select-all`}
                className="h-4 w-4 cursor-pointer accent-gold"
              />
            </th>
            {columns.map((c) => (
              <th key={c.label} className={`px-3 py-2.5 ${c.className || ""}`}>{c.label}</th>
            ))}
            <th className="px-3 py-2.5 text-right">Actions</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => {
            const id = keyFor(r);
            const isSel = selectedIds.has(id);
            return (
              <tr
                key={id}
                className={`border-b border-border/60 last:border-b-0 hover:bg-elevated/40 ${isSel ? "bg-gold/5" : ""}`}
                data-testid={`${testId}-row-${id}`}
              >
                <td className="px-3 py-2.5">
                  <input
                    type="checkbox"
                    aria-label={`Select row`}
                    checked={isSel}
                    onChange={() => onToggle(id)}
                    data-testid={`${testId}-select-${id}`}
                    className="h-4 w-4 cursor-pointer accent-gold"
                  />
                </td>
                {columns.map((c) => (
                  <td key={c.label} className={`px-3 py-2.5 align-top ${c.className || ""}`}>{c.get(r)}</td>
                ))}
                <td className="px-3 py-2.5 text-right">
                  <div className="flex justify-end gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => onEdit(r)}>Edit</Button>
                    <Button size="sm" variant="ghost" onClick={() => onDelete(r)}>Delete</Button>
                  </div>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}


const ASSET_LIST_COLUMNS = [
  { label: "Name", get: (r) => <span className="font-semibold text-text">{r.name}</span> },
  { label: "Type", get: (r) => <span className="text-muted">{r.category} · {r.item_type}</span> },
  {
    label: "Current value",
    className: "text-right",
    get: (r) => <span className="font-mono text-text">{formatINR(r.current_value)}</span>,
  },
  {
    label: "Gain",
    className: "text-right",
    get: (r) => {
      if (r.gain == null) return <span className="text-muted">—</span>;
      const cls = r.gain >= 0 ? "text-emerald-400" : "text-red-400";
      return <span className={`font-mono ${cls}`}>{r.gain >= 0 ? "+" : ""}{formatINR(r.gain).replace("₹ ", "")}</span>;
    },
  },
];

const LIABILITY_LIST_COLUMNS = [
  { label: "Name", get: (r) => <span className="font-semibold text-text">{r.name}</span> },
  { label: "Type", get: (r) => <span className="text-muted">{r.liability_label || "—"}</span> },
  { label: "Lender", get: (r) => <span className="text-muted">{r.lender || "—"}</span> },
  {
    label: "Outstanding",
    className: "text-right",
    get: (r) => <span className="font-mono text-text">{formatINR(r.outstanding_amount)}</span>,
  },
  {
    label: "EMI",
    className: "text-right",
    get: (r) => (
      r.emi_amount != null
        ? <span className="font-mono text-muted">{formatINR(r.emi_amount)}</span>
        : <span className="text-muted">—</span>
    ),
  },
];


function AssetListView({ rows, allSelected, someSelected, onToggleAll, selectedIds, onToggle, onEdit, onDelete }) {
  return (
    <DenseListTable
      testId="asset-list"
      rows={rows}
      keyFor={(r) => r.id}
      allSelected={allSelected}
      someSelected={someSelected}
      onToggleAll={onToggleAll}
      selectedIds={selectedIds}
      onToggle={onToggle}
      onEdit={onEdit}
      onDelete={onDelete}
      columns={ASSET_LIST_COLUMNS}
    />
  );
}


function LiabilityListView({ rows, allSelected, someSelected, onToggleAll, selectedIds, onToggle, onEdit, onDelete }) {
  return (
    <DenseListTable
      testId="liab-list"
      rows={rows}
      keyFor={(r) => r.id}
      allSelected={allSelected}
      someSelected={someSelected}
      onToggleAll={onToggleAll}
      selectedIds={selectedIds}
      onToggle={onToggle}
      onEdit={onEdit}
      onDelete={onDelete}
      columns={LIABILITY_LIST_COLUMNS}
    />
  );
}




function BucketTab({ active, label, count, total, onClick, testKey }) {
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      onClick={onClick}
      data-testid={`asset-bucket-${testKey}`}
      className={`whitespace-nowrap rounded-full border px-3 py-1 text-xs transition ${
        active
          ? "border-gold/60 bg-gold/10 text-gold"
          : "border-border bg-elevated/40 text-muted hover:border-gold/30 hover:text-text"
      }`}
    >
      <span className="font-medium">{label}</span>
      <span className="ml-1.5 text-[10px] opacity-70">{count}</span>
      {total > 0 && (
        <span className="ml-1.5 text-[10px] font-mono opacity-70">· {formatINRCompact(total)}</span>
      )}
    </button>
  );
}

function AssetCard({ asset, onEdit, onDelete, selected, onToggleSelect }) {
  const behaviorColor =
    asset.behavior === "depreciating"
      ? "text-red-400"
      : asset.behavior === "stable"
      ? "text-muted"
      : "text-emerald-400";
  const delta = asset.gain;
  const deltaPct = (delta != null && asset.purchase_price > 0)
    ? (delta / asset.purchase_price) * 100
    : 0;
  return (
    <Card
      data-testid={`asset-card-${asset.id}`}
      className={`border-border transition-colors hover:border-gold/40 ${selected ? "border-gold/60 bg-gold/5" : ""}`}
    >
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={onToggleSelect}
                aria-label={`Select ${asset.name}`}
                data-testid={`asset-select-${asset.id}`}
                className="mt-1 h-4 w-4 cursor-pointer accent-gold"
              />
            )}
            <div>
              <div className="text-sm font-semibold text-text">{asset.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted">
                {asset.category} · {asset.item_type}
              </div>
            </div>
          </div>
          <span className={`text-[10px] uppercase tracking-widest ${behaviorColor}`}>
            {asset.behavior}
          </span>
        </div>

        <div className="rounded-md border border-border bg-elevated/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Current value</div>
          <div className="mt-1 text-2xl font-semibold text-gold">
            {formatINR(asset.current_value)}
          </div>
          {delta != null && (
            <div className={`mt-1 text-xs ${delta >= 0 ? "text-emerald-400" : "text-red-400"}`}>
              {delta >= 0 ? "+" : ""}
              {formatINR(delta).replace("₹ ", "")} ₹ ({deltaPct.toFixed(2)}%)
            </div>
          )}
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-muted">
          <div>
            <div className="uppercase tracking-widest text-[10px]">Bought</div>
            <div className="text-text">
              {asset.purchase_date || "—"}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px]">Cost</div>
            <div className="text-text">{asset.purchase_price != null ? formatINR(asset.purchase_price) : "—"}</div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px]">Liquidity</div>
            <div className="text-text capitalize">{asset.liquidity}</div>
          </div>
          {asset.gain != null && (
            <div>
              <div className="uppercase tracking-widest text-[10px]">Gain</div>
              <div className={asset.gain >= 0 ? "text-emerald-400" : "text-red-400"}>
                {asset.gain >= 0 ? "+" : ""}{formatINR(asset.gain).replace("₹ ", "₹")}
              </div>
            </div>
          )}
        </div>

        {asset.notes && (
          <p className="rounded border border-border/70 bg-bg/30 p-2 text-xs italic text-muted">
            {asset.notes}
          </p>
        )}

        <div className="mt-auto flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`asset-edit-${asset.id}`}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            data-testid={`asset-delete-${asset.id}`}
            className="gap-1"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function AssetDialog({ initial, mode, types, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        asset_type_id: initial.asset_type_id,
        name: initial.name,
        purchase_date: initial.purchase_date || "",
        purchase_price: String(initial.purchase_price ?? ""),
        current_value: initial.current_value != null ? String(initial.current_value) : "",
        notes: initial.notes || "",
      };
    }
    // Leave the type picker empty on new-asset so the search box is the first thing users
    // interact with — no more accidentally saving as the alphabetically-first type.
    return { ...ASSET_EMPTY, asset_type_id: "" };
  });
  const [submitting, setSubmitting] = useState(false);
  const [typeQuery, setTypeQuery] = useState("");
  const [typePickerOpen, setTypePickerOpen] = useState(false);

  const selectedType = types.find((t) => t.id === form.asset_type_id);

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  function chooseType(id) {
    setForm((prev) => ({ ...prev, asset_type_id: id }));
    setTypeQuery("");
    setTypePickerOpen(false);
  }

  async function onSubmit(e) {
    e.preventDefault();
    // Either Purchase price OR Present value must be filled — at least one is required
    // so we always have a number to sum on the net-worth widget. The 13-Feb-2026 rewrite
    // removed rate-based appreciation entirely; when both are entered, the gain shown on
    // the row is the pure `current − purchase` delta.
    const priceEntered = form.purchase_price !== "" && Number(form.purchase_price) > 0;
    const currentEntered =
      form.current_value !== "" && Number(form.current_value) > 0;
    if (!priceEntered && !currentEntered) {
      toast.error("Enter either the Purchase price or the Present value.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = {
        asset_type_id: form.asset_type_id,
        name: form.name.trim(),
        purchase_date: form.purchase_date || null,
        purchase_price: priceEntered ? Number(form.purchase_price) : null,
        current_value: currentEntered ? Number(form.current_value) : null,
        notes: form.notes.trim() || null,
      };
      if (mode === "edit" && initial) {
        await updateAsset(initial.id, payload);
      } else {
        await createAsset(payload);
      }
      toast.success("Saved.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    } finally {
      setSubmitting(false);
    }
  }

  // Group types by category, then filter by the free-text query. We match against
  // `item_type` (the primary label) and `category` so a user typing "mutual" finds every
  // Mutual Fund variant, and "invest" surfaces the whole Investments category.
  const grouped = useMemo(() => {
    const q = typeQuery.trim().toLowerCase();
    const filtered = q
      ? types.filter((t) => {
          const hay = `${t.item_type || ""} ${t.category || ""}`.toLowerCase();
          return hay.includes(q);
        })
      : types;
    const g = new Map();
    for (const t of filtered) {
      if (!g.has(t.category)) g.set(t.category, []);
      g.get(t.category).push(t);
    }
    return [...g.entries()];
  }, [types, typeQuery]);

  const totalMatches = grouped.reduce((s, [, items]) => s + items.length, 0);

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="asset-dialog"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <h3 className="text-lg font-semibold text-text">
            {mode === "edit" ? "Edit asset" : "Add asset"}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label htmlFor="asset-form-type-search">Asset type *</Label>
            <div className="relative">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted" />
              <Input
                id="asset-form-type-search"
                type="text"
                value={
                  typePickerOpen
                    ? typeQuery
                    : selectedType
                    ? `${selectedType.item_type} (${selectedType.class === "financial" ? "Fin" : "Phy"})`
                    : ""
                }
                onFocus={() => {
                  setTypePickerOpen(true);
                  setTypeQuery("");
                }}
                onBlur={() => {
                  // Delay close so option clicks register before the list unmounts.
                  window.setTimeout(() => setTypePickerOpen(false), 120);
                }}
                onChange={(e) => setTypeQuery(e.target.value)}
                placeholder="Search asset type… e.g. mutual fund, gold, real estate"
                className="pl-9"
                data-testid="asset-form-type-search"
                autoComplete="off"
              />
              {typePickerOpen && (
                <div
                  className="absolute left-0 right-0 top-full z-20 mt-1 max-h-72 overflow-y-auto rounded-md border border-border bg-card shadow-xl"
                  data-testid="asset-form-type-list"
                >
                  {totalMatches === 0 ? (
                    <div className="px-3 py-4 text-center text-xs text-muted">
                      No types match &quot;{typeQuery.trim()}&quot;.
                    </div>
                  ) : (
                    grouped.map(([cat, items]) => (
                      <div key={cat} className="border-b border-border last:border-b-0">
                        <div className="sticky top-0 bg-elevated/60 px-3 py-1.5 text-[10px] font-semibold uppercase tracking-widest text-muted">
                          {cat}
                        </div>
                        {items.map((t) => (
                          <button
                            key={t.id}
                            type="button"
                            // onMouseDown fires *before* the input's onBlur, so the click
                            // is registered even though blur closes the menu.
                            onMouseDown={(e) => {
                              e.preventDefault();
                              chooseType(t.id);
                            }}
                            data-testid={`asset-form-type-option-${t.id}`}
                            className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-gold/10 hover:text-gold ${
                              t.id === form.asset_type_id ? "bg-gold/10 text-gold" : "text-text"
                            }`}
                          >
                            <span>{t.item_type}</span>
                            <span className="text-[10px] uppercase tracking-widest text-muted">
                              {t.class === "financial" ? "Fin" : "Phy"}
                            </span>
                          </button>
                        ))}
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            {/* Hidden required input keeps native form validation working — a user can't
                submit until they've picked a type. */}
            <input
              type="text"
              value={form.asset_type_id}
              required
              tabIndex={-1}
              onChange={() => {}}
              className="pointer-events-none absolute h-0 w-0 opacity-0"
              aria-hidden
              data-testid="asset-form-type"
            />
            {selectedType && (
              <p className="text-[11px] text-muted">
                Behavior: <span className="text-text">{selectedType.behavior}</span> · Liquidity:{" "}
                <span className="text-text capitalize">{selectedType.liquidity}</span>
                {selectedType.income_generating ? " · Income-generating" : ""}
              </p>
            )}
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Nickname *</Label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. HDFC Small Cap SIP, Baleno, Andheri Flat"
              required
              data-testid="asset-form-name"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Purchase date</Label>
            <Input type="date" value={form.purchase_date} onChange={set("purchase_date")} data-testid="asset-form-purchase-date" />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Purchase price ₹ <span className="text-[10px] font-normal text-muted">(or fill Present value below)</span></Label>
            <Input
              type="number"
              min="0"
              value={form.purchase_price}
              onChange={set("purchase_price")}
              placeholder="What you paid when you bought it"
              data-testid="asset-form-price"
            />
            <CurrencyHint value={form.purchase_price} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Present value ₹ <span className="text-[10px] font-normal text-muted">(or fill Purchase price above)</span></Label>
            <Input
              type="number"
              min="0"
              value={form.current_value}
              onChange={set("current_value")}
              placeholder="Today's market value"
              data-testid="asset-form-current-value"
            />
            <CurrencyHint value={form.current_value} />
            {/* Live gain hint — only when BOTH fields are filled. Pure delta, no rate math. */}
            {form.purchase_price !== "" && form.current_value !== "" && (() => {
              const gain = Number(form.current_value) - Number(form.purchase_price);
              const pct = Number(form.purchase_price) > 0 ? (gain / Number(form.purchase_price)) * 100 : 0;
              const positive = gain >= 0;
              return (
                <div
                  className={`text-[11px] ${positive ? "text-emerald-400" : "text-red-400"}`}
                  data-testid="asset-form-gain"
                >
                  Gain: {positive ? "+" : ""}₹{Math.round(gain).toLocaleString("en-IN")}
                  {" "}({positive ? "+" : ""}{pct.toFixed(2)}%)
                </div>
              );
            })()}
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Notes</Label>
            <Input
              value={form.notes}
              onChange={set("notes")}
              placeholder="Optional context"
              data-testid="asset-form-notes"
            />
          </div>

          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="asset-form-submit">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

/* ------------------------------ Liabilities Tab ------------------------------ */

const LIAB_EMPTY = {
  liability_type_id: "",
  name: "",
  lender: "",
  principal_amount: "",
  outstanding_amount: "",
  interest_rate: "",
  emi_amount: "",
  start_date: "",
  tenure_months: "",
  notes: "",
};

function LiabilitiesTab({ onChange }) {
  const [types, setTypes] = useState([]);
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialog, setDialog] = useState(null);
  const [query, setQuery] = useState("");
  const [viewMode, setViewMode] = useState("grid");
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [busyDelete, setBusyDelete] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, r] = await Promise.all([fetchLiabilityTypes(), fetchLiabilities()]);
      setTypes(t);
      setRows(r);
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't load liabilities."));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(row) {
    setConfirmDialog({ mode: "single", row });
  }

  async function confirmSingleDelete() {
    const row = confirmDialog?.row;
    if (!row) return;
    setBusyDelete(true);
    try {
      await deleteLiability(row.id);
      toast.success("Deleted.");
      setSelectedIds((prev) => {
        const next = new Set(prev);
        next.delete(row.id);
        return next;
      });
      await load();
      await onChange?.();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete."));
    } finally {
      setBusyDelete(false);
      setConfirmDialog(null);
    }
  }

  async function confirmBulkDelete() {
    const ids = confirmDialog?.ids || [];
    if (!ids.length) return;
    setBusyDelete(true);
    try {
      const { deleted } = await bulkDeleteLiabilities(ids);
      toast.success(`${deleted} liabilit${deleted === 1 ? "y" : "ies"} deleted.`);
      setSelectedIds(new Set());
      await load();
      await onChange?.();
    } catch (e) {
      toast.error(normalizeDetail(e.response?.data?.detail, "Couldn't delete the selected liabilities."));
    } finally {
      setBusyDelete(false);
      setConfirmDialog(null);
    }
  }

  const total = useMemo(
    () => rows.reduce((s, l) => s + Number(l.outstanding_amount || 0), 0),
    [rows]
  );

  const filteredRows = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return rows;
    return rows.filter((l) => {
      // Search across name + liability type label + lender so users can find a row by
      // typing "car", "home loan", "hdfc" etc — matches the user's mental model.
      const hay = `${l.name || ""} ${l.liability_label || ""} ${l.lender || ""}`.toLowerCase();
      return hay.includes(q);
    });
  }, [rows, query]);

  // ---------- Selection helpers ----------
  const visibleIds = useMemo(() => filteredRows.map((r) => r.id), [filteredRows]);
  const allVisibleSelected = visibleIds.length > 0 && visibleIds.every((id) => selectedIds.has(id));
  const someVisibleSelected = visibleIds.some((id) => selectedIds.has(id)) && !allVisibleSelected;

  useEffect(() => {
    setSelectedIds((prev) => {
      const visible = new Set(rows.map((r) => r.id));
      let dirty = false;
      const next = new Set();
      for (const id of prev) {
        if (visible.has(id)) next.add(id);
        else dirty = true;
      }
      return dirty ? next : prev;
    });
  }, [rows]);

  function toggleSelect(id) {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleSelectAllVisible() {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (allVisibleSelected) {
        for (const id of visibleIds) next.delete(id);
      } else {
        for (const id of visibleIds) next.add(id);
      }
      return next;
    });
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex items-center justify-between">
        <div className="text-sm text-muted">
          {rows.length} liabilit{rows.length === 1 ? "y" : "ies"} · Outstanding {formatINR(total)}
        </div>
        <Button
          onClick={() => setDialog({ mode: "create" })}
          data-testid="liab-add-btn"
          className="gap-2"
          disabled={!types.length}
        >
          <Plus className="h-4 w-4" /> Add liability
        </Button>
      </div>

      {!types.length && !loading && (
        <div className="rounded-lg border border-amber-400/30 bg-amber-400/5 p-4 text-sm text-amber-200">
          No liability types are configured yet. Please ask an admin to set up the master taxonomy.
        </div>
      )}

      {rows.length > 0 && (
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <SearchBox
            value={query}
            onChange={setQuery}
            placeholder="Search liabilities by name, type or lender…"
            testId="liab-search"
          />
          <div className="flex items-center gap-1 rounded-md border border-border bg-elevated/40 p-1 text-[10px]">
            <button
              type="button"
              onClick={() => setViewMode("grid")}
              className={`rounded px-2.5 py-1 uppercase tracking-widest ${viewMode === "grid" ? "bg-gold/15 text-gold" : "text-muted hover:text-text"}`}
              data-testid="liab-view-grid"
            >
              Grid
            </button>
            <button
              type="button"
              onClick={() => setViewMode("list")}
              className={`rounded px-2.5 py-1 uppercase tracking-widest ${viewMode === "list" ? "bg-gold/15 text-gold" : "text-muted hover:text-text"}`}
              data-testid="liab-view-list"
            >
              List
            </button>
          </div>
        </div>
      )}

      {selectedIds.size > 0 && (
        <div
          className="flex items-center justify-between rounded-md border border-red-500/30 bg-red-500/5 px-3 py-2"
          data-testid="liab-bulk-bar"
        >
          <div className="text-sm">
            <span className="font-semibold text-gold">{selectedIds.size}</span> liabilit
            {selectedIds.size === 1 ? "y" : "ies"} selected
          </div>
          <div className="flex gap-2">
            <Button
              size="sm"
              variant="ghost"
              onClick={() => setSelectedIds(new Set())}
              data-testid="liab-bulk-clear"
            >
              Clear
            </Button>
            <Button
              size="sm"
              variant="destructive"
              onClick={() => setConfirmDialog({ mode: "bulk", ids: [...selectedIds] })}
              data-testid="liab-bulk-delete"
            >
              <Trash2 className="mr-1.5 h-3.5 w-3.5" /> Delete selected
            </Button>
          </div>
        </div>
      )}

      {loading ? (
        <div className="rounded-lg border border-border p-8 text-center text-muted">Loading…</div>
      ) : rows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted">
          No liabilities yet. Add a loan, EMI or credit-card balance to see it in your net-worth
          snapshot.
        </div>
      ) : filteredRows.length === 0 ? (
        <div className="rounded-lg border border-dashed border-border p-8 text-center text-muted">
          No liabilities match &quot;{query.trim()}&quot;.
        </div>
      ) : viewMode === "list" ? (
        <LiabilityListView
          rows={filteredRows}
          allSelected={allVisibleSelected}
          someSelected={someVisibleSelected}
          onToggleAll={toggleSelectAllVisible}
          selectedIds={selectedIds}
          onToggle={toggleSelect}
          onEdit={(r) => setDialog({ mode: "edit", row: r })}
          onDelete={handleDelete}
        />
      ) : (
        <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
          {filteredRows.map((r) => (
            <LiabilityCard
              key={r.id}
              row={r}
              selected={selectedIds.has(r.id)}
              onToggleSelect={() => toggleSelect(r.id)}
              onEdit={() => setDialog({ mode: "edit", row: r })}
              onDelete={() => handleDelete(r)}
            />
          ))}
        </div>
      )}

      {dialog && (
        <LiabilityDialog
          initial={dialog.row}
          mode={dialog.mode}
          types={types}
          onClose={() => setDialog(null)}
          onSaved={async () => {
            setDialog(null);
            await load();
            await onChange?.();
          }}
        />
      )}

      <DeleteConfirmDialog
        open={confirmDialog?.mode === "single"}
        busy={busyDelete}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={confirmSingleDelete}
        title={`Delete "${confirmDialog?.row?.name || "liability"}"?`}
        description="This permanently removes the liability from your balance sheet."
        confirmLabel="Yes, delete"
        testId="liab-single-delete-dialog"
      />
      <DeleteConfirmDialog
        open={confirmDialog?.mode === "bulk"}
        busy={busyDelete}
        onCancel={() => setConfirmDialog(null)}
        onConfirm={confirmBulkDelete}
        title={`Delete ${confirmDialog?.ids?.length || 0} liabilit${confirmDialog?.ids?.length === 1 ? "y" : "ies"}?`}
        description="Every selected liability will be permanently removed from your balance sheet. To prevent accidental bulk deletion, please type DELETE below to confirm."
        requireType
        confirmLabel="Yes, delete all"
        testId="liab-bulk-delete-dialog"
      />
    </div>
  );
}

function LiabilityCard({ row, onEdit, onDelete, selected, onToggleSelect }) {
  const paid = Math.max(0, row.principal_amount - row.outstanding_amount);
  const pctPaid = row.principal_amount > 0
    ? Math.min(100, (paid / row.principal_amount) * 100)
    : 0;
  return (
    <Card
      data-testid={`liab-card-${row.id}`}
      className={`border-border transition-colors hover:border-gold/40 ${selected ? "border-gold/60 bg-gold/5" : ""}`}
    >
      <CardContent className="flex h-full flex-col gap-3 p-5">
        <div className="flex items-start justify-between gap-2">
          <div className="flex items-start gap-2.5">
            {onToggleSelect && (
              <input
                type="checkbox"
                checked={!!selected}
                onChange={onToggleSelect}
                aria-label={`Select ${row.name}`}
                data-testid={`liab-select-${row.id}`}
                className="mt-1 h-4 w-4 cursor-pointer accent-gold"
              />
            )}
            <div>
              <div className="text-sm font-semibold text-text">{row.name}</div>
              <div className="text-[11px] uppercase tracking-widest text-muted">
                {row.liability_label}
              </div>
              {row.lender && (
                <div className="mt-0.5 text-xs text-muted">Lender: {row.lender}</div>
              )}
            </div>
          </div>
          <Landmark className="h-4 w-4 text-gold" />
        </div>

        <div className="rounded-md border border-border bg-elevated/40 p-3">
          <div className="text-[10px] uppercase tracking-widest text-muted">Outstanding</div>
          <div className="mt-1 text-2xl font-semibold text-red-300">
            {formatINR(row.outstanding_amount)}
          </div>
          <div className="mt-2 h-1.5 rounded-full bg-border">
            <div
              className="h-full rounded-full bg-emerald-500"
              style={{ width: `${pctPaid}%` }}
              aria-label={`${pctPaid.toFixed(0)}% paid`}
            />
          </div>
          <div className="mt-1 text-[10px] text-muted">
            {pctPaid.toFixed(0)}% paid off ({formatINR(paid)})
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 text-xs text-muted">
          <div>
            <div className="uppercase tracking-widest text-[10px]">Principal</div>
            <div className="text-text">{formatINR(row.principal_amount)}</div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px]">EMI</div>
            <div className="text-text">{row.emi_amount ? formatINR(row.emi_amount) : "—"}</div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px]">Interest</div>
            <div className="text-text">
              {row.interest_rate != null ? `${Number(row.interest_rate).toFixed(2)}%` : "—"}
            </div>
          </div>
          <div>
            <div className="uppercase tracking-widest text-[10px]">Tenure</div>
            <div className="text-text">
              {row.tenure_months ? `${row.tenure_months} mo` : "—"}
            </div>
          </div>
        </div>

        {row.notes && (
          <p className="rounded border border-border/70 bg-bg/30 p-2 text-xs italic text-muted">
            {row.notes}
          </p>
        )}

        <div className="mt-auto flex justify-end gap-2 pt-2">
          <Button size="sm" variant="outline" onClick={onEdit} data-testid={`liab-edit-${row.id}`}>
            Edit
          </Button>
          <Button
            size="sm"
            variant="ghost"
            onClick={onDelete}
            data-testid={`liab-delete-${row.id}`}
            className="gap-1"
          >
            <Trash2 className="h-3.5 w-3.5" /> Delete
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function LiabilityDialog({ initial, mode, types, onClose, onSaved }) {
  const [form, setForm] = useState(() => {
    if (initial) {
      return {
        liability_type_id: initial.liability_type_id,
        name: initial.name,
        lender: initial.lender || "",
        principal_amount: String(initial.principal_amount ?? ""),
        outstanding_amount: String(initial.outstanding_amount ?? ""),
        interest_rate: initial.interest_rate != null ? String(initial.interest_rate) : "",
        emi_amount: initial.emi_amount != null ? String(initial.emi_amount) : "",
        start_date: initial.start_date || "",
        tenure_months: initial.tenure_months != null ? String(initial.tenure_months) : "",
        notes: initial.notes || "",
      };
    }
    return { ...LIAB_EMPTY, liability_type_id: types[0]?.id || "" };
  });
  const [submitting, setSubmitting] = useState(false);

  function set(field) {
    return (e) => setForm((prev) => ({ ...prev, [field]: e.target.value }));
  }

  async function onSubmit(e) {
    e.preventDefault();
    setSubmitting(true);
    try {
      const payload = {
        liability_type_id: form.liability_type_id,
        name: form.name.trim(),
        lender: form.lender.trim() || null,
        principal_amount: Number(form.principal_amount) || 0,
        outstanding_amount: Number(form.outstanding_amount) || 0,
        interest_rate: form.interest_rate === "" ? null : Number(form.interest_rate),
        emi_amount: form.emi_amount === "" ? null : Number(form.emi_amount),
        start_date: form.start_date || null,
        tenure_months: form.tenure_months === "" ? null : Number(form.tenure_months),
        notes: form.notes.trim() || null,
      };
      if (mode === "edit" && initial) {
        await updateLiability(initial.id, payload);
      } else {
        await createLiability(payload);
      }
      toast.success("Saved.");
      await onSaved();
    } catch (err) {
      toast.error(normalizeDetail(err.response?.data?.detail, "Couldn't save."));
    } finally {
      setSubmitting(false);
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center p-4"
      role="dialog"
      aria-modal="true"
      data-testid="liab-dialog"
    >
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" onClick={onClose} aria-hidden />
      <div className="relative max-h-[92vh] w-full max-w-2xl overflow-y-auto rounded-2xl border border-gold/30 bg-card shadow-2xl">
        <div className="sticky top-0 z-10 flex items-center justify-between border-b border-border bg-card/95 px-6 py-4 backdrop-blur">
          <h3 className="text-lg font-semibold text-text">
            {mode === "edit" ? "Edit liability" : "Add liability"}
          </h3>
          <button
            onClick={onClose}
            className="rounded-md border border-border p-1.5 text-muted hover:border-gold hover:text-gold"
            aria-label="Close"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
        <form onSubmit={onSubmit} className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2">
          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Liability type *</Label>
            <SelectNative
              value={form.liability_type_id}
              onChange={set("liability_type_id")}
              required
              data-testid="liab-form-type"
            >
              <option value="">Choose…</option>
              {types.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </SelectNative>
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Nickname *</Label>
            <Input
              value={form.name}
              onChange={set("name")}
              placeholder="e.g. Home Loan – HDFC"
              required
              data-testid="liab-form-name"
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Lender</Label>
            <Input
              value={form.lender}
              onChange={set("lender")}
              placeholder="e.g. HDFC Bank, Bajaj Finserv"
            />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Principal ₹ *</Label>
            <Input
              type="number"
              min="0"
              value={form.principal_amount}
              onChange={set("principal_amount")}
              required
              data-testid="liab-form-principal"
            />
            <CurrencyHint value={form.principal_amount} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Outstanding ₹ *</Label>
            <Input
              type="number"
              min="0"
              value={form.outstanding_amount}
              onChange={set("outstanding_amount")}
              required
              data-testid="liab-form-outstanding"
            />
            <CurrencyHint value={form.outstanding_amount} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Interest rate %</Label>
            <Input
              type="number"
              step="0.1"
              min="0"
              max="100"
              value={form.interest_rate}
              onChange={set("interest_rate")}
            />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>EMI ₹</Label>
            <Input
              type="number"
              min="0"
              value={form.emi_amount}
              onChange={set("emi_amount")}
            />
            <CurrencyHint value={form.emi_amount} />
          </div>

          <div className="flex flex-col gap-1.5">
            <Label>Start date</Label>
            <Input type="date" value={form.start_date} onChange={set("start_date")} />
          </div>
          <div className="flex flex-col gap-1.5">
            <Label>Tenure (months)</Label>
            <Input
              type="number"
              min="0"
              max="600"
              value={form.tenure_months}
              onChange={set("tenure_months")}
            />
          </div>

          <div className="flex flex-col gap-1.5 sm:col-span-2">
            <Label>Notes</Label>
            <Input value={form.notes} onChange={set("notes")} placeholder="Optional context" />
          </div>

          <div className="flex justify-end gap-2 sm:col-span-2">
            <Button type="button" variant="outline" onClick={onClose}>
              Cancel
            </Button>
            <Button type="submit" disabled={submitting} data-testid="liab-form-submit">
              {submitting ? "Saving…" : "Save"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}

export default NetWorthPage;

// Reference unused icons (kept for possible future badges) to satisfy the tree-shaker linter.
export const __icons = { Coins, Layers };
